import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Loader2, AlertTriangle, CheckCircle2, XCircle, Copy, Download, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { formatCurrency } from '../utils/helpers';

const FLAG_STYLES = {
  error: 'bg-red-50 border-red-300 text-red-800',
  warning: 'bg-amber-50 border-amber-300 text-amber-800',
  info: 'bg-slate-50 border-slate-300 text-slate-700',
};

const Money = ({ value, bold }) => (
  <span className={bold ? 'font-semibold tabular-nums' : 'tabular-nums'}>{formatCurrency(value || 0)}</span>
);

const SectionTitle = ({ children, right }) => (
  <div className="flex items-baseline justify-between border-b border-navy/10 pb-1 mb-2">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-navy/70">{children}</h4>
    {right}
  </div>
);

const AccommodationRow = ({ row, testid }) => (
  <div className="rounded-md border border-navy/10 p-3 space-y-2" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-navy truncate">
          {row.place} · {row.hotel_name}
        </p>
        <p className="text-xs text-navy/60">
          {[row.room_name, row.room_category, row.meal_plan, `${row.nights}N`, row.rooms ? `${row.rooms} room(s)` : null]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
      <Money value={row.total} bold />
    </div>

    {(row.segments || []).map((seg, i) => (
      <div key={i} className="rounded bg-slate-50 p-2 text-xs text-navy/80 space-y-1">
        <div className="flex justify-between">
          <span className="font-medium">{seg.segment} · {seg.nights}N</span>
          <Money value={seg.total} />
        </div>
        <p className="text-[11px] leading-relaxed text-navy/70">{seg.formula}</p>
        <p className="text-[10px] text-navy/50">
          {[seg.source?.season_name, seg.source?.meal_plan, seg.source?.valid_from && `valid ${seg.source.valid_from} → ${seg.source.valid_to}`]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
    ))}

    {row.formula && !row.segments?.length && (
      <p className="rounded bg-slate-50 p-2 text-[11px] text-navy/70">{row.formula}</p>
    )}

    {row.resolution?.flag && row.resolution.flag !== 'match' && (
      <p className="text-[11px] text-amber-700">Hotel resolution: {row.resolution.flag}</p>
    )}
    {row.check && !row.check.ok && (
      <p className="text-[11px] text-red-700">
        Recomputed {formatCurrency(row.check.expected)} vs stored {formatCurrency(row.check.actual)}
      </p>
    )}
  </div>
);

const CategoryAudit = ({ cat }) => (
  <div className="space-y-5">
    <div>
      <SectionTitle right={<Money value={cat.accommodation_total} bold />}>Accommodation</SectionTitle>
      <div className="space-y-2">
        {cat.accommodation.length === 0 && <p className="text-xs text-navy/50">No hotel rows stored.</p>}
        {cat.accommodation.map((row, i) => (
          <AccommodationRow key={i} row={row} testid={`audit-hotel-row-${i}`} />
        ))}
      </div>
    </div>

    {cat.transport && (
      <div data-testid="audit-transport">
        <SectionTitle right={<Money value={cat.transport.total} bold />}>Transport</SectionTitle>
        <div className="rounded-md border border-navy/10 p-3 text-xs space-y-1">
          <p className="text-sm font-medium text-navy">
            {cat.transport.vehicle_label} · {cat.transport.vehicle_count} vehicle(s)
          </p>
          <p className="text-navy/70">{cat.transport.basis}</p>
          <p className="text-[11px] text-navy/70">{cat.transport.formula}</p>
          <p className="text-[10px] text-navy/50">
            {[
              cat.transport.contract_type,
              cat.transport.gst_slab ? `GST slab ${cat.transport.gst_slab}%${cat.transport.gst_applied ? ' (applied)' : ' (not applied)'}` : null,
              cat.transport.source?.valid_from && `valid ${cat.transport.source.valid_from} → ${cat.transport.source.valid_to}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      </div>
    )}

    {cat.p2p?.length > 0 && (
      <div>
        <SectionTitle>Point-to-point transfers</SectionTitle>
        {cat.p2p.map((r, i) => (
          <div key={i} className="flex justify-between text-xs py-1 border-b border-navy/5">
            <span className="text-navy/80">{r.label} · {r.formula}{r.gst_pct ? ` + ${r.gst_pct}% GST` : ''}</span>
            <Money value={r.total} />
          </div>
        ))}
      </div>
    )}

    {cat.misc?.length > 0 && (
      <div>
        <SectionTitle>Miscellaneous</SectionTitle>
        {cat.misc.map((r, i) => (
          <div key={i} className="flex justify-between text-xs py-1 border-b border-navy/5">
            <span className="text-navy/80">
              {r.label} · {r.formula}
              {r.gst_pct ? ` + ${r.gst_pct}% GST` : ''}
              {r.is_optional ? ' · optional' : ''}
            </span>
            <Money value={r.total} />
          </div>
        ))}
      </div>
    )}

    <div data-testid="audit-waterfall">
      <SectionTitle>Cost build-up</SectionTitle>
      <div className="space-y-1">
        {cat.waterfall.map((s, i) => (
          <div
            key={i}
            className={`flex items-baseline justify-between gap-4 text-xs py-1 ${
              s.emphasis ? 'border-t border-navy/15 pt-1.5 font-medium text-navy' : 'text-navy/75'
            }`}
          >
            <span className="min-w-0">
              {s.label}
              <span className="ml-2 text-[10px] text-navy/45">{s.formula}</span>
            </span>
            <Money value={s.amount} bold={s.emphasis} />
          </div>
        ))}
      </div>
    </div>

    <div data-testid="audit-checks">
      <SectionTitle>Integrity checks</SectionTitle>
      <div className="space-y-1">
        {cat.checks.map((c) => (
          <div key={c.code} className="flex items-center justify-between text-xs py-0.5">
            <span className="flex items-center gap-1.5 text-navy/75">
              {c.ok ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              ) : (
                <XCircle className="w-3.5 h-3.5 text-red-600" />
              )}
              {c.label}
            </span>
            <span className={`tabular-nums ${c.ok ? 'text-navy/60' : 'text-red-700'}`}>
              {formatCurrency(c.actual)}
              {!c.ok && ` vs ${formatCurrency(c.expected)}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const toCsv = (audit) => {
  const lines = [['Category', 'Section', 'Item', 'Detail', 'Amount'].join(',')];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  audit.categories.forEach((cat) => {
    cat.accommodation.forEach((r) => {
      const detail = (r.segments || []).map((s) => s.formula).join(' | ') || r.formula || '';
      lines.push([cat.category, 'Accommodation', `${r.place} - ${r.hotel_name}`, detail, r.total].map(esc).join(','));
    });
    if (cat.transport) {
      lines.push([cat.category, 'Transport', cat.transport.vehicle_label, cat.transport.formula, cat.transport.total].map(esc).join(','));
    }
    (cat.p2p || []).forEach((r) => lines.push([cat.category, 'P2P', r.label, r.formula, r.total].map(esc).join(',')));
    (cat.misc || []).forEach((r) => lines.push([cat.category, 'Misc', r.label, r.formula, r.total].map(esc).join(',')));
    cat.waterfall.forEach((s) => lines.push([cat.category, 'Build-up', s.label, s.formula, s.amount].map(esc).join(',')));
  });
  return lines.join('\n');
};

export const CostAuditPanel = ({ quotationId, quoteReference, open, onOpenChange }) => {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAllFlags, setShowAllFlags] = useState(false);

  useEffect(() => {
    if (!open || !quotationId || audit) return;
    setLoading(true);
    api
      .get(`/quotations/${quotationId}/cost-audit`)
      .then((res) => setAudit(res.data))
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Unable to load cost audit');
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, quotationId, audit, onOpenChange]);

  const download = () => {
    const blob = new Blob([toCsv(audit)], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${quoteReference || 'quotation'}-cost-audit.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const copy = () => {
    navigator.clipboard.writeText(toCsv(audit).replace(/","/g, ' | ').replace(/"/g, ''));
    toast.success('Cost audit copied');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="cost-audit-panel">
        <SheetHeader>
          <SheetTitle className="text-navy">Cost audit · {quoteReference}</SheetTitle>
        </SheetHeader>

        {loading && (
          <div className="flex items-center gap-2 py-10 text-sm text-navy/60">
            <Loader2 className="w-4 h-4 animate-spin" /> Building audit trace…
          </div>
        )}

        {!loading && audit && (
          <div className="mt-4 space-y-4">
            <div className="rounded-md bg-slate-50 border border-navy/10 p-3 text-xs text-navy/75 space-y-1">
              <p>
                <span className="font-medium">{audit.package_type.replace(/_/g, ' ')}</span> ·{' '}
                {audit.trip.nights}N / {audit.trip.days}D · {audit.trip.tour_start_date} → {audit.trip.tour_end_date}
              </p>
              <p>
                {audit.pax.adults} adult(s), {audit.pax.children_with_bed} child w/bed,{' '}
                {audit.pax.children_no_bed} child no-bed, {audit.pax.infants} infant(s) ·{' '}
                <span className="font-medium">{audit.pax.paying_pax} paying pax</span> · rooms:{' '}
                {audit.pax.single_rooms}S / {audit.pax.double_rooms}D / {audit.pax.triple_rooms}T
              </p>
              <p className="flex items-center gap-1 text-[11px] text-navy/50">
                <Info className="w-3 h-3" /> Derived from the saved snapshot — masters are not re-priced.
              </p>
            </div>

            {audit.flags.length > 0 && (
              <div className="space-y-1.5" data-testid="audit-flags">
                {(showAllFlags ? audit.flags : audit.flags.slice(0, 3)).map((f, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-2 rounded border p-2 text-xs ${FLAG_STYLES[f.level] || FLAG_STYLES.info}`}
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>{f.message}</span>
                  </div>
                ))}
                {audit.flags.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllFlags((v) => !v)}
                    className="text-xs text-navy/60 underline hover:text-navy"
                    data-testid="audit-flags-toggle"
                  >
                    {showAllFlags ? 'Show fewer' : `Show ${audit.flags.length - 3} more finding(s)`}
                  </button>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={copy} data-testid="audit-copy-btn">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
              </Button>
              <Button size="sm" variant="outline" onClick={download} data-testid="audit-csv-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
              </Button>
            </div>

            {audit.categories.length > 0 ? (
              <Tabs defaultValue={audit.categories[0].category}>
                <TabsList className="flex-wrap h-auto">
                  {audit.categories.map((c) => (
                    <TabsTrigger
                      key={c.category}
                      value={c.category}
                      className="text-xs"
                      data-testid={`audit-tab-${c.category}`}
                    >
                      {c.category}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {audit.categories.map((c) => (
                  <TabsContent key={c.category} value={c.category} className="mt-4">
                    <CategoryAudit cat={c} />
                  </TabsContent>
                ))}
              </Tabs>
            ) : (
              <p className="text-sm text-navy/60">No category pricing stored on this quotation.</p>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CostAuditPanel;
